<template>
<b-tab lazy title-item-class="mb-1" class="p-0">
    <template slot="title">Forms</template>

    <b-card-group deck class="mb-3">
        <b-row class="w-100 m-0">
            <b-col v-for="form in forms" :key="form.id" cols="12" md="6" lg="4" class="mb-3">
                <b-card class="h-100 card-accent-secondary" no-body>
                    <b-card-body class="d-flex flex-column">
                        <div class="d-flex align-items-start mb-2">
                            <i :class="['fa-light', form.icon, 'fa-2x', 'mr-2', 'text-primary']"></i>
                            <div>
                                <h5 class="mb-1">{{ form.title }}</h5>
                                <small class="text-muted">{{ form.subtitle }}</small>
                            </div>
                        </div>
                        <p class="small text-muted flex-grow-1">{{ form.description }}</p>
                        <div>
                            <b-button size="sm" variant="primary" @click="open(form.id)">
                                <i class="fa-solid fa-pen-to-square"></i> Fill &amp; Download
                            </b-button>
                            <b-button size="sm" variant="outline-secondary" :href="form.templateUrl" target="_blank">
                                <i class="fa-solid fa-file-pdf"></i> Blank PDF
                            </b-button>
                        </div>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>
    </b-card-group>

    <b-card class="card-accent-secondary" no-body>
        <template v-slot:header>
            <i class="fa-light fa-clock-rotate-left"></i> <strong>Previously generated</strong>
        </template>
        <b-table
            :items="savedForms"
            :fields="savedFields"
            show-empty
            empty-text="No forms have been generated for this patient yet."
            small
            responsive
            class="mb-0">
            <template v-slot:cell(actions)="row">
                <b-button size="sm" variant="primary" class="mr-2" v-b-tooltip.hover title="Reprint" @click="reprint(row.item)">
                    <i class="fa-solid fa-print"></i> Reprint
                </b-button>
                <b-button size="sm" variant="outline-danger" v-b-tooltip.hover title="Delete" @click="removeForm(row.item)">
                    <i class="fa-solid fa-trash"></i>
                </b-button>
            </template>
        </b-table>
    </b-card>

    <!-- One modal per form so each has its own state -->
    <b-modal v-for="form in forms" :key="`modal-${form.id}`"
             :id="`patient-form-modal-${form.id}`"
             :title="form.title"
             size="xl"
             hide-footer
             scrollable>
        <component v-if="activeForm === form.id"
                   :is="form.component"
                   :demographics="demographics"
                   @close="close" />
    </b-modal>
</b-tab>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import TabExtension from "./TabExtension"
import { fillAndDownload } from "./forms/pdfFiller"

import FalabMedicalForm from "./forms/FalabMedicalForm"
import NisSicknessForm from "./forms/NisSicknessForm"
import TeacherSickLeaveForm from "./forms/TeacherSickLeaveForm"
import DemeraraMutualForm from "./forms/DemeraraMutualForm"
import GtmHealthForm from "./forms/GtmHealthForm"
import PublicAssistanceForm from "./forms/PublicAssistanceForm"

export default TabExtension.extend({
    name: "patient-forms",
    components: {
        FalabMedicalForm,
        NisSicknessForm,
        TeacherSickLeaveForm,
        DemeraraMutualForm,
        GtmHealthForm,
        PublicAssistanceForm,
    },
    data() {
        return {
            activeForm: null,
            forms: [
                {
                    id: "falab",
                    title: "FALAB Medical Report",
                    subtitle: "Firearms Licensing Approval Board",
                    description: "Medical/physical examination for a firearm licence application or renewal.",
                    icon: "fa-shield-halved",
                    component: "FalabMedicalForm",
                    templateUrl: "/forms/falab-medical.pdf",
                },
                {
                    id: "nis-sickness",
                    title: "NIS Sickness Benefit",
                    subtitle: "National Insurance & Social Security",
                    description: "Claim for sickness benefit with attached medical certificate.",
                    icon: "fa-notes-medical",
                    component: "NisSicknessForm",
                    templateUrl: "/forms/nis-sickness-benefit.pdf",
                },
                {
                    id: "teacher-sick-leave",
                    title: "Teacher's Sick Leave",
                    subtitle: "Medical Certificate",
                    description: "Short medical certificate for a teacher's sick leave request.",
                    icon: "fa-chalkboard-user",
                    component: "TeacherSickLeaveForm",
                    templateUrl: "/forms/teacher-sick-leave.pdf",
                },
                {
                    id: "demerara-mutual",
                    title: "Demerara Mutual Health Claim",
                    subtitle: "Demerara Mutual Life",
                    description: "Health claim form for medical, dental and vision services.",
                    icon: "fa-file-medical",
                    component: "DemeraraMutualForm",
                    templateUrl: "/forms/demerara-mutual-health-claim.pdf",
                },
                {
                    id: "gtm-health",
                    title: "GTM Health Insurance Claim",
                    subtitle: "Guyana & Trinidad Mutual",
                    description: "Health insurance claim form completed by doctor/dentist/optometrist/pharmacist.",
                    icon: "fa-hand-holding-medical",
                    component: "GtmHealthForm",
                    templateUrl: "/forms/gtm-health-insurance-claim.pdf",
                },
                {
                    id: "public-assistance",
                    title: "Public Assistance Medical",
                    subtitle: "Ministry of Human Services & Social Security",
                    description: "Application for Public Assistance — Medical / Economical.",
                    icon: "fa-hand-holding-heart",
                    component: "PublicAssistanceForm",
                    templateUrl: "/forms/public-assistance-medical.pdf",
                },
            ],
        }
    },
    computed: {
        ...mapGetters("patients", ["getPatientDemographics"]),
        ...mapGetters("patientForms", ["getPatientForms"]),
        demographics() {
            return this.getPatientDemographics()
        },
        savedForms() {
            return this.getPatientForms()
        },
        savedFields() {
            return [
                { key: "created", label: "Generated", formatter: (v) => (v ? new Date(v).toLocaleString() : "") },
                { key: "title", label: "Form" },
                { key: "created_by_name", label: "By" },
                { key: "actions", label: "", class: "text-right" },
            ]
        },
    },
    methods: {
        ...mapActions("patientForms", ["getFormsForCurrentPatient", "deletePatientForm"]),
        open(id) {
            this.activeForm = id
            this.$bvModal.show(`patient-form-modal-${id}`)
        },
        close() {
            if (this.activeForm) {
                this.$bvModal.hide(`patient-form-modal-${this.activeForm}`)
                this.activeForm = null
            }
        },
        async reprint(item) {
            if (!item.render_payload || !item.render_payload.templateUrl) {
                this.$bvToast.toast("This form has no stored print data and cannot be reprinted.", {
                    title: "Cannot reprint", variant: "warning",
                })
                return
            }
            try {
                await fillAndDownload(item.render_payload)
            } catch (e) {
                this.$bvToast.toast(e.message || "Could not regenerate the PDF.", {
                    title: "Reprint failed", variant: "danger",
                })
            }
        },
        async removeForm(item) {
            const ok = await this.$bvModal.msgBoxConfirm(
                `Delete the generated "${item.title}" form from ${item.created ? new Date(item.created).toLocaleDateString() : ""}?`,
                { title: "Delete generated form", okVariant: "danger", okTitle: "Delete", cancelTitle: "Cancel", centered: true }
            )
            if (ok) {
                await this.deletePatientForm(item.id)
            }
        },
    },
    mounted() {
        this.getFormsForCurrentPatient()
    },
})
</script>
